import { useNavigate } from "react-router-dom";
import { ROUTES } from "../shared/utils";


export const slugify = (value: string): string => {

    const result = value
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')          // spaces → -
        .replace(/[^\w-]/g, '')        // remove special chars
        .replace(/-+/g, '-')          // collapse multiple - 
        .replace(/^-+|-+$/g, '');     // remove leading/trailing dashes
        
    return result;
};

export const useNavigation = () => {

    const navigate = useNavigate();

    const goToPortfolioProjects = (portfolioName: string, portfolioId: number) => {
        navigate(ROUTES.PORTFOLIO_PROJECTS.replaceAll(':portfolioName', slugify(portfolioName)).replaceAll(':portfolioId', String(portfolioId)));
    };

    const goToPortfolioDetails = (portfolioName: string, portfolioId: number) => {
        navigate(ROUTES.PORTFOLIO_PROGRAMME_DASHBOARD.replaceAll(':portfolioName', slugify(portfolioName)).replaceAll(':portfolioId', String(portfolioId).replaceAll(' ', '-')));
    };

    const goToProjectDetails = (projectId: number, projectName: string) => {
        navigate(ROUTES.PROJECTS_DETAILS.replaceAll(':projectId', String(projectId)).replaceAll(':projectName', slugify(projectName)).replaceAll(':projectSection', String('dashboard')));
    };

    const goToProjectDetailsFromPortfolio = (portfolioName: string, portfolioId: string, projectId: string, projectName: string) => {
        navigate(ROUTES.PORTFOLIO_PROJECT_DETAILS.replaceAll(':portfolioName', slugify(portfolioName)).replaceAll(':portfolioId', String(portfolioId)).replaceAll(':projectId', String(projectId)).replaceAll(':projectName', slugify(projectName)).replaceAll(':projectSection', 'dashboard'));
    };

    const goToJeopardyDetails = (jeopardyName: string, jeopardyId: number) => {
        navigate(ROUTES.JEOPARDY_MANAGER_DETAILS.replaceAll(':jeopardyName', slugify(jeopardyName)).replaceAll(':jeopardyId', String(jeopardyId)));
    };

    const goBack = () => {
        navigate(-1);
    };

    const goToAddProject = ({ programme, portfolio, portfolio_name }: { programme?: string, portfolio?: string, portfolio_name?: string }) => {
        if (programme && portfolio && portfolio_name) {
            navigate(ROUTES.ADD_PROJECT.concat(`?programme=${programme}&portfolio=${portfolio}&portfolio_name=${portfolio_name}`));
        } else {
            navigate(ROUTES.ADD_PROJECT);
        }
    };

    const goToProjectListing = () => {
        navigate(ROUTES.PROJECTS);
    };

    const goToEditProject = (projectId: string) => {
        navigate(ROUTES.EDIT_PROJECT.replaceAll(':projectId', projectId));
    };

    const goToRecurringTasks = ({ projectId, projectName }: { projectId: number, projectName: string }) => {
        navigate(ROUTES.PROJECTS_DETAILS_RECURRING_TASKS.replaceAll(':projectId', String(projectId)).replaceAll(':projectName', slugify(projectName)));
    };

    const goToSchedulePlanning = ({ projectId, projectName, objectId, type }: { projectId: number, projectName: string, objectId: number, type: string }) => {
        navigate(ROUTES.PROJECTS_DETAILS_SCHEDULE_AND_PLANNING.replaceAll(':projectId', String(projectId)).replaceAll(':projectName', slugify(projectName)).concat(`?id=${objectId}&type=${type}`));
    };

    const goToStageGates = ({ projectId, projectName, objectId }: { projectId: number, projectName: string, objectId: number }) => {
        navigate(ROUTES.PROJECTS_DETAILS_STAGE_GATES.replaceAll(':projectId', String(projectId)).replaceAll(':projectName', slugify(projectName)).concat(`?selected=true&id=${objectId}`));
    };

    const goToProjectRiskDashboard = ({ projectId, projectName, riskId }: { projectId: number, projectName: string, riskId: string }) => {
        navigate(ROUTES.PROJECTS_DETAILS_RISKS_AND_ISSUES.replaceAll(':projectId', String(projectId)).replaceAll(':projectName', slugify(projectName)).concat(`?tab=risk-register&selected=true&id=${projectId}`));
    }

    const goToProjectQueries = ({ projectId, projectName, queryId }: { projectId: number, projectName: string, queryId: string }) => {
        navigate(ROUTES.PROJECTS_DETAILS_QUERIES.replaceAll(':projectId', String(projectId)).replaceAll(':projectName', slugify(projectName)).concat(`?selected=true&id=${queryId}`));
    };

    const goToProjectArtefacts = ({ projectId, projectName, artefactId }: { projectId: number, projectName: string, artefactId: string}) => {
        navigate(ROUTES.PROJECTS_DETAILS_ARTEFACT_AND_DOCUMENTS.replaceAll(':projectId', String(projectId)).replaceAll(':projectName', slugify(projectName)).concat(`?selected=true&id=${artefactId}`));
    };

    const goToRiskDashboard = ({ id }: { id?: string }) => {
        if (id) {
            navigate(ROUTES.RISK_DASHBOARD.concat(`?tab=risk-register&selected=true&id=${id}`));
        } else {
            navigate(ROUTES.RISK_DASHBOARD);
        }
    };

    const goToQueries = (projectId: number) => {
        navigate(`/queries?selected=true&id=${projectId}`);
    };

    const goToMeetingDetails = (meetingId: number) => {
        navigate(`/meeting-manager?tab=meetingDetails&meeting_id=${meetingId}`);
    };

    const goToAddPortfolio = () => {
        navigate(ROUTES.ADD_PORTFOLIO);
    };

    const goToEditPortfolio = ({ portfolioId, portfolioName }: { portfolioId: number, portfolioName: string }) => {
        navigate(ROUTES.EDIT_PORTFOLIO.replaceAll(':portfolioName', slugify(portfolioName)).replaceAll(':portfolioId', String(portfolioId)));
    };

    const goToProjectTaskDetails = ({ projectId, projectName, taskId }: { projectId: number, projectName: string, taskId: number }) => {
        navigate(ROUTES.PROJECTS_DETAILS_TASKS.replaceAll(':projectId', String(projectId)).replaceAll(':projectName', slugify(projectName)).concat(`?selected=true&taskId=${taskId}`));
    };

    const goToTaskDetails = (taskId: number) => {
        navigate(`/tasks?selected=true&taskId=${taskId}`);
    };

    const goToMaterialRequestDetails = ({projectId, projectName, requestId} : {projectId: string, projectName: string, requestId: string}) => {
        navigate(ROUTES.PROJECTS_DETAILS_MATERIAL_REQUESTS.replaceAll(':projectId', projectId).replaceAll(':projectName', slugify(projectName)).concat(`?details=true&requestId=${requestId}`));
    };

    const goToResourceRequestDetails = ({projectId, projectName, requestId} : {projectId: string, projectName: string, requestId: string}) => {
        navigate(ROUTES.PROJECTS_DETAILS_RESOURCE_REQUESTS.replaceAll(':projectId', projectId).replaceAll(':projectName', slugify(projectName)).concat(`?details=true&requestId=${requestId}`));
    };

    const goToEditMaterialRequest = ({projectId, projectName, requestId} : {projectId: string, projectName: string, requestId: string}) => {
        navigate(ROUTES.PROJECTS_DETAILS_MATERIAL_REQUESTS.replaceAll(':projectId', projectId).replaceAll(':projectName', slugify(projectName)).concat(`?edit=true&requestId=${requestId}`));
    };

    const goToEditResourceRequest = ({projectId, projectName, requestId} : {projectId: string, projectName: string, requestId: string}) => {
        navigate(ROUTES.PROJECTS_DETAILS_RESOURCE_REQUESTS.replaceAll(':projectId', projectId).replaceAll(':projectName', slugify(projectName)).concat(`?edit=true&requestId=${requestId}`));
    };

    return {
        goBack,
        goToPortfolioProjects,
        goToProjectDetails,
        goToJeopardyDetails,
        goToPortfolioDetails,
        goToAddProject,
        goToEditProject,
        goToRecurringTasks,
        goToSchedulePlanning,
        goToStageGates,
        goToProjectArtefacts,
        goToRiskDashboard,
        goToQueries,
        goToMeetingDetails,
        goToAddPortfolio,
        goToEditPortfolio,
        goToProjectDetailsFromPortfolio,
        goToProjectListing,
        goToProjectRiskDashboard,
        goToProjectQueries,
        goToProjectTaskDetails,
        goToTaskDetails,
        goToMaterialRequestDetails,
        goToResourceRequestDetails,
        goToEditMaterialRequest,
        goToEditResourceRequest
    };

};